"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DateInput = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const react_select_1 = __importDefault(require("react-select"));
const styled_components_1 = __importDefault(require("styled-components"));
const theme_1 = require("../../theme");
const lodash_1 = require("lodash");
const YearContext_1 = require("../../contexts/YearContext");
const DateSelectWrapper = styled_components_1.default.div `
  display: flex;
  width: 100%;
  gap: ${theme_1.theme.spacing(1)};
`;
const DateSelectColumn = styled_components_1.default.div `
  flex: ${(p) => p.flex};
  min-width: 0;
`;
const dateSelectStyles = {
    control: (baseStyles, state) => ({
        ...baseStyles,
        minHeight: "40px",
        height: "40px",
        borderColor: theme_1.theme.colors.grey(3),
        boxShadow: "none",
        backgroundColor: state.isDisabled
            ? theme_1.theme.colors.grey(4)
            : theme_1.theme.colors.white,
        "&:hover": {
            borderColor: theme_1.theme.colors.grey(3),
        },
    }),
    valueContainer: (baseStyles) => ({
        ...baseStyles,
        height: "40px",
        padding: "0 8px",
    }),
    indicatorsContainer: (baseStyles) => ({
        ...baseStyles,
        height: "40px",
    }),
    indicatorSeparator: () => ({
        display: "none",
    }),
    menu: (baseStyles) => ({
        ...baseStyles,
        zIndex: 10,
    }),
    singleValue: (baseStyles, state) => ({
        ...baseStyles,
        color: state.isDisabled ? theme_1.theme.colors.grey(2) : theme_1.theme.colors.blackText,
        fontSize: theme_1.theme.fontSizeNumber(-1),
    }),
    placeholder: (baseStyles) => ({
        ...baseStyles,
        color: theme_1.theme.colors.grey(2),
        fontSize: theme_1.theme.fontSizeNumber(-1),
    }),
    input: (baseStyles) => ({
        ...baseStyles,
        fontSize: theme_1.theme.fontSizeNumber(-1),
        color: theme_1.theme.colors.blackText,
        margin: 0,
        paddingBottom: 0,
        paddingTop: 0,
    }),
};
const monthOptions = Array.from({ length: 12 }, (_, i) => ({
    value: i + 1,
    label: new Date(2000, i, 1).toLocaleString("en-US", { month: "long" }),
}));
const getDaysInMonth = (year, month) => {
    return new Date(year, month, 0).getDate();
};
const formatDateValue = (year, month, day) => {
    return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
};
const parseDateValue = (value) => {
    if (!value) {
        return {
            year: null,
            month: null,
            day: null,
        };
    }
    const w3cDateMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (w3cDateMatch) {
        const parsedYear = Number(w3cDateMatch[1]);
        const parsedMonth = Number(w3cDateMatch[2]);
        const parsedDay = Number(w3cDateMatch[3]);
        if (Number.isInteger(parsedYear) &&
            parsedMonth >= 1 &&
            parsedMonth <= 12 &&
            parsedDay >= 1 &&
            parsedDay <= getDaysInMonth(parsedYear, parsedMonth)) {
            return {
                year: parsedYear,
                month: parsedMonth,
                day: parsedDay,
            };
        }
        return {
            year: null,
            month: null,
            day: null,
        };
    }
    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) {
        return {
            year: null,
            month: null,
            day: null,
        };
    }
    return {
        year: parsedDate.getFullYear(),
        month: parsedDate.getMonth() + 1,
        day: parsedDate.getDate(),
    };
};
const createYearOptions = (years) => {
    const minYear = Math.min(...years);
    const maxYear = Math.max(...years);
    const options = [];
    for (let optionYear = maxYear; optionYear >= minYear; optionYear -= 1) {
        options.push({
            value: optionYear,
            label: String(optionYear),
        });
    }
    return options;
};
const createDayOptions = (daysInMonth) => {
    return Array.from({ length: daysInMonth }, (_, i) => {
        const value = i + 1;
        return {
            value,
            label: String(value).padStart(2, "0"),
        };
    });
};
const DateInput = (props) => {
    const { value, onChange, debounceTime } = props;
    const { years } = (0, YearContext_1.useYear)();
    const parsedValue = (0, react_1.useMemo)(() => parseDateValue(value), [value]);
    const [selectedYear, setSelectedYear] = (0, react_1.useState)(parsedValue.year);
    const [selectedMonth, setSelectedMonth] = (0, react_1.useState)(parsedValue.month);
    const [selectedDay, setSelectedDay] = (0, react_1.useState)(parsedValue.day);
    (0, react_1.useEffect)(() => {
        setSelectedYear(parsedValue.year);
        setSelectedMonth(parsedValue.month);
        setSelectedDay(parsedValue.day);
    }, [parsedValue.day, parsedValue.month, parsedValue.year]);
    const sendChange = (0, react_1.useMemo)(() => (0, lodash_1.debounce)((nextValue) => {
        if (onChange) {
            onChange(nextValue);
        }
    }, debounceTime ?? 1000), [debounceTime, onChange]);
    (0, react_1.useEffect)(() => () => {
        sendChange.cancel();
    }, [sendChange]);
    const yearOptions = (0, react_1.useMemo)(() => createYearOptions(years), [years]);
    const dayOptions = (0, react_1.useMemo)(() => {
        if (selectedYear !== null && selectedMonth !== null) {
            return createDayOptions(getDaysInMonth(selectedYear, selectedMonth));
        }
        return createDayOptions(31);
    }, [selectedMonth, selectedYear]);
    const emitDate = (nextYear, nextMonth, nextDay) => {
        if (nextYear !== null && nextMonth !== null && nextDay !== null) {
            sendChange(formatDateValue(nextYear, nextMonth, nextDay));
        }
    };
    const onYearChange = (selectedOption) => {
        const nextYear = selectedOption?.value ?? null;
        let nextDay = selectedDay;
        if (nextYear !== null && selectedMonth !== null && nextDay !== null) {
            const maxDay = getDaysInMonth(nextYear, selectedMonth);
            if (nextDay > maxDay) {
                nextDay = maxDay;
            }
        }
        setSelectedYear(nextYear);
        setSelectedDay(nextDay);
        emitDate(nextYear, selectedMonth, nextDay);
    };
    const onMonthChange = (selectedOption) => {
        const nextMonth = selectedOption?.value ?? null;
        let nextDay = selectedDay;
        if (selectedYear !== null && nextMonth !== null && nextDay !== null) {
            const maxDay = getDaysInMonth(selectedYear, nextMonth);
            if (nextDay > maxDay) {
                nextDay = maxDay;
            }
        }
        setSelectedMonth(nextMonth);
        setSelectedDay(nextDay);
        emitDate(selectedYear, nextMonth, nextDay);
    };
    const onDayChange = (selectedOption) => {
        const nextDay = selectedOption?.value ?? null;
        setSelectedDay(nextDay);
        emitDate(selectedYear, selectedMonth, nextDay);
    };
    return ((0, jsx_runtime_1.jsxs)(DateSelectWrapper, { children: [(0, jsx_runtime_1.jsx)(DateSelectColumn, { flex: 1.2, children: (0, jsx_runtime_1.jsx)(react_select_1.default, { placeholder: "Year", isSearchable: false, options: yearOptions, value: selectedYear !== null
                        ? {
                            value: selectedYear,
                            label: String(selectedYear),
                        }
                        : null, onChange: onYearChange, styles: dateSelectStyles }) }), (0, jsx_runtime_1.jsx)(DateSelectColumn, { flex: 1.4, children: (0, jsx_runtime_1.jsx)(react_select_1.default, { placeholder: "Month", isSearchable: false, options: monthOptions, value: selectedMonth !== null
                        ? monthOptions.find((option) => option.value === selectedMonth) ??
                            null
                        : null, onChange: onMonthChange, styles: dateSelectStyles }) }), (0, jsx_runtime_1.jsx)(DateSelectColumn, { flex: 1, children: (0, jsx_runtime_1.jsx)(react_select_1.default, { placeholder: "Day", isSearchable: false, options: dayOptions, value: selectedDay !== null
                        ? dayOptions.find((option) => option.value === selectedDay) ??
                            null
                        : null, onChange: onDayChange, styles: dateSelectStyles, isDisabled: selectedYear === null || selectedMonth === null }) })] }));
};
exports.DateInput = DateInput;
